import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import PdfPreviewDialog from '@/components/PdfPreviewDialog';
import DocumentBuilderDialog from '@/components/DocumentBuilderDialog';
import ConvertToClientDialog from '@/components/ConvertToClientDialog';
import {
  Receipt, Plus, Eye, PaperPlaneTilt, CurrencyCircleDollar, Trash, Prohibit,
  MagnifyingGlass, Gear, BellRinging, Clock, CheckCircle, UserPlus,
} from '@phosphor-icons/react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  unpaid: 'bg-yellow-100 text-yellow-700',
  partially_paid: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-400 line-through',
};

const money = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

const SummaryCard = ({ icon: Icon, label, value, sub, colour }) => (
  <Card data-testid={`invoice-summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
          <p className="text-xl font-bold text-[#0A192F] mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className="w-9 h-9 flex items-center justify-center" style={{ backgroundColor: `${colour}1A` }}>
          <Icon className="w-5 h-5" style={{ color: colour }} />
        </div>
      </div>
    </CardContent>
  </Card>
);


const AdminInvoices = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [cases, setCases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [convertDoc, setConvertDoc] = useState(null);

  const [payOpen, setPayOpen] = useState(null); // invoice object
  const [payForm, setPayForm] = useState({ amount: '', paid_on: today(), method: 'EFT', reference: '' });
  const [amountOpen, setAmountOpen] = useState(null); // uploaded invoice whose value is being captured
  const [amountValue, setAmountValue] = useState('');

  const fetchData = async () => {
    try {
      const [iRes, cRes, csRes, sRes, setRes] = await Promise.all([
        api.get('/invoices'), api.get('/companies'), api.get('/cases'),
        api.get('/invoicing/summary'), api.get('/invoicing/settings'),
      ]);
      setInvoices(iRes.data);
      setCompanies(cRes.data);
      setCases(csRes.data);
      setSummary(sRes.data);
      setSettings(setRes.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Prefill from a quotation ("Create invoice from quotation")
  useEffect(() => {
    const companyId = searchParams.get('company_id');
    const quotationId = searchParams.get('quotation_id');
    if (!companyId && !quotationId) return;
    setPrefill({
      company_id: companyId || '',
      case_id: searchParams.get('case_id') || 'none',
      quotation_id: quotationId || null,
      reference: searchParams.get('reference') || (quotationId ? `Quotation ${quotationId}` : ''),
      line_items: [{
        description: searchParams.get('description') || 'Consulting services as per quotation',
        quantity: 1,
        unit_price: searchParams.get('amount') || '',
      }],
    });
    setCreateOpen(true);
    setSearchParams(new URLSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCreate = () => {
    setPrefill(null);
    setCreateOpen(true);
  };


  const sendInvoice = async (inv) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${inv.invoice_id}/send`);
      data.email_sent ? toast.success(data.message) : toast.warning(data.message);
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const openPayment = (inv) => {
    setPayForm({ amount: String(inv.balance ?? inv.total ?? inv.amount ?? ''), paid_on: today(), method: 'EFT', reference: '' });
    setPayOpen(inv);
  };

  const recordPayment = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${payOpen.invoice_id}/payments`, {
        amount: Number(payForm.amount),
        paid_on: payForm.paid_on,
        method: payForm.method,
        reference: payForm.reference || null,
      });
      toast.success('Payment recorded');
      setPayOpen(null);
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const openAmount = (inv) => {
    setAmountValue(inv.amount ? String(inv.amount) : '');
    setAmountOpen(inv);
  };

  const saveAmount = async () => {
    setBusy(true);
    try {
      await api.put(`/invoices/${amountOpen.invoice_id}?amount=${Number(amountValue)}`);
      toast.success('Invoice amount captured');
      setAmountOpen(null);
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancelInvoice = async (inv) => {
    if (!window.confirm(`Cancel invoice ${inv.invoice_number}? It stays on record but is excluded from all totals.`)) return;
    try {
      await api.post(`/invoices/${inv.invoice_id}/cancel`);
      toast.success('Invoice cancelled');
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm('Delete this invoice permanently?')) return;
    try {
      await api.delete(`/invoices/${inv.invoice_id}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runReminders = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/invoices/reminders/run');
      toast.success(`Reminders run — ${data.due_reminders_sent} due, ${data.overdue_reminders_sent} overdue notices sent`);
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const statusOf = (i) => i.display_status || i.status || 'unpaid';

  const filtered = invoices
    .filter((i) => (tab === 'all' ? true : tab === 'open'
      ? ['sent', 'unpaid', 'partially_paid', 'overdue'].includes(statusOf(i))
      : statusOf(i) === tab))
    .filter((i) => {
      const q = search.toLowerCase();
      return !q || (i.company_name || '').toLowerCase().includes(q)
        || (i.invoice_number || '').toLowerCase().includes(q)
        || (i.description || '').toLowerCase().includes(q);
    });

  const counts = {
    all: invoices.length,
    open: invoices.filter((i) => ['sent', 'unpaid', 'partially_paid', 'overdue'].includes(statusOf(i))).length,
    draft: invoices.filter((i) => statusOf(i) === 'draft').length,
    overdue: invoices.filter((i) => statusOf(i) === 'overdue').length,
    paid: invoices.filter((i) => statusOf(i) === 'paid').length,
  };

  if (loading) {
    return (
      <AdminLayout title="Invoicing">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Invoicing">
      <div className="space-y-6 fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F]">Invoicing</h1>
            <p className="text-sm text-gray-500 mt-1">Create VAT invoices, track payments and chase overdue accounts</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={runReminders} disabled={busy} className="rounded-none" data-testid="run-reminders-btn">
              <BellRinging className="w-4 h-4 mr-2" /> Run Reminders
            </Button>
            <Link to="/admin/invoicing-settings">
              <Button variant="outline" className="rounded-none" data-testid="invoice-settings-link">
                <Gear className="w-4 h-4 mr-2" /> Settings
              </Button>
            </Link>
            <Button onClick={openCreate} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="new-invoice-btn">
              <Plus className="w-4 h-4 mr-2" /> New Invoice
            </Button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <SummaryCard icon={Receipt} label="Invoiced" value={money(summary.invoiced_total)} sub={`${summary.invoiced_count} issued`} colour="#3B82F6" />
            <SummaryCard icon={CheckCircle} label="Paid" value={money(summary.paid_total)} colour="#4CAF50" />
            <SummaryCard icon={Clock} label="Outstanding" value={money(summary.outstanding_total)} colour="#F59E0B" />
            <SummaryCard icon={BellRinging} label="Overdue" value={money(summary.overdue_total)} sub={`${summary.overdue_count} invoice(s)`} colour="#EF4444" />
            <SummaryCard icon={Receipt} label="Drafts" value={summary.draft_count} sub="not yet sent" colour="#6B7280" />
          </div>
        )}

        <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
          {[['all', 'All'], ['open', 'Open'], ['draft', 'Drafts'], ['overdue', 'Overdue'], ['paid', 'Paid']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === key ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              data-testid={`invoice-tab-${key}`}
            >
              {label} ({counts[key]})
            </button>
          ))}
        </div>

        <div className="relative max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by client, number or description"
            className="pl-9 rounded-none"
            data-testid="invoice-search-input"
          />
        </div>

        <Card>
          <CardContent className="pt-6 overflow-x-auto">
            <table className="w-full data-table" data-testid="invoices-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-3 px-3">Invoice</th>
                  <th className="py-3 px-3">Client</th>
                  <th className="py-3 px-3">Issued</th>
                  <th className="py-3 px-3">Due</th>
                  <th className="py-3 px-3 text-right">Total</th>
                  <th className="py-3 px-3 text-right">Balance</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => {
                  const generated = i.source === 'generated';
                  const status = statusOf(i);
                  return (
                    <tr key={i.invoice_id} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`invoice-row-${i.invoice_id}`}>
                      <td className="py-3 px-3">
                        <p className="font-medium text-[#0A192F]">{i.invoice_number || '—'}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[200px]">{i.description || i.original_filename || ''}</p>
                      </td>
                      <td className="py-3 px-3 text-[#4B5563]">{i.company_name}</td>
                      <td className="py-3 px-3 text-sm text-[#4B5563]">{(i.issue_date || i.created_at || '').slice(0, 10)}</td>
                      <td className="py-3 px-3 text-sm text-[#4B5563]">{(i.due_date || '').slice(0, 10) || '—'}</td>
                      <td className="py-3 px-3 text-right font-medium">
                        {generated ? money(i.total ?? i.amount) : (
                          <button
                            onClick={() => openAmount(i)}
                            title="Capture or correct the invoice amount"
                            className={Number(i.amount || 0) > 0
                              ? 'underline decoration-dotted underline-offset-4 hover:text-[#4CAF50]'
                              : 'text-red-600 underline decoration-dotted underline-offset-4'}
                            data-testid={`edit-amount-btn-${i.invoice_id}`}
                          >
                            {Number(i.amount || 0) > 0 ? money(i.amount) : 'Capture amount'}
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-3 text-right">{generated ? money(i.balance) : '—'}</td>
                      <td className="py-3 px-3">
                        <Badge className={`${STATUS_COLORS[status] || 'bg-gray-100'} rounded-none`} data-testid={`invoice-status-${i.invoice_id}`}>
                          {status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="icon" title="View PDF"
                            onClick={() => setPreview({
                              url: generated ? `/api/invoices/${i.invoice_id}/pdf` : `/api/invoices/${i.invoice_id}/download`,
                              title: `Invoice ${i.invoice_number || ''} — ${i.company_name}`,
                              filename: generated ? `Invoice-${i.invoice_number}.pdf` : i.original_filename,
                            })}
                            data-testid={`view-invoice-btn-${i.invoice_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {generated && status !== 'cancelled' && (
                            <Button
                              variant="ghost" size="icon" title="Email to client" disabled={busy}
                              onClick={() => sendInvoice(i)} data-testid={`send-invoice-btn-${i.invoice_id}`}
                            >
                              <PaperPlaneTilt className="w-4 h-4 text-[#0055FF]" />
                            </Button>
                          )}
                          {status !== 'paid' && status !== 'cancelled' && (
                            <Button
                              variant="ghost" size="icon" title="Record payment"
                              onClick={() => (generated ? openPayment(i) : api.put(`/invoices/${i.invoice_id}?status=paid`)
                                .then(() => { toast.success('Invoice marked paid'); fetchData(); })
                                .catch((e) => toast.error(formatApiError(e))))}
                              data-testid={`pay-invoice-btn-${i.invoice_id}`}
                            >
                              <CurrencyCircleDollar className="w-4 h-4 text-[#4CAF50]" />
                            </Button>
                          )}
                          {generated && !['draft', 'cancelled'].includes(status) && (
                            <Button
                              variant="ghost" size="icon" title="Cancel invoice"
                              onClick={() => cancelInvoice(i)} data-testid={`cancel-invoice-btn-${i.invoice_id}`}
                            >
                              <Prohibit className="w-4 h-4 text-amber-600" />
                            </Button>
                          )}
                          {i.ad_hoc_client && (
                            <Button
                              variant="ghost" size="icon" title="Turn into a full client with portal access"
                              onClick={() => setConvertDoc(i)} data-testid={`convert-client-btn-${i.invoice_id}`}
                            >
                              <UserPlus className="w-4 h-4 text-[#0055FF]" />
                            </Button>
                          )}
                          {(!generated || ['draft', 'cancelled'].includes(status)) && (
                            <Button
                              variant="ghost" size="icon" title="Delete"
                              onClick={() => deleteInvoice(i)} data-testid={`delete-invoice-btn-${i.invoice_id}`}
                            >
                              <Trash className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-12" data-testid="invoices-empty">
                <Receipt className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
                <p className="text-[#4B5563]">No invoices here yet</p>
                <Button onClick={openCreate} className="mt-4 bg-[#4CAF50] hover:bg-[#45a049] rounded-none">
                  <Plus className="w-4 h-4 mr-2" /> Create your first invoice
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <DocumentBuilderDialog
          kind="invoice"
          open={createOpen}
          onOpenChange={setCreateOpen}
          companies={companies}
          cases={cases}
          settings={settings}
          prefill={prefill}
          onCreated={fetchData}
        />

        {/* Record payment dialog */}
        <Dialog open={!!payOpen} onOpenChange={(open) => !open && setPayOpen(null)}>
          <DialogContent className="sm:max-w-[460px]">
            <DialogHeader>
              <DialogTitle>Record Payment — {payOpen?.invoice_number}</DialogTitle>
              <DialogDescription>Part-payments are allowed; the balance and status update automatically.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-gray-600">
                Outstanding balance: <strong>{money(payOpen?.balance ?? payOpen?.total)}</strong>
              </p>
              <div>
                <Label className="text-sm font-semibold">Amount received (R) *</Label>
                <Input type="number" min="0" step="any" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="rounded-none mt-1" data-testid="payment-amount-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Date paid</Label>
                  <Input type="date" value={payForm.paid_on} onChange={(e) => setPayForm({ ...payForm, paid_on: e.target.value })} className="rounded-none mt-1" data-testid="payment-date-input" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Method</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['EFT', 'Cash', 'Card', 'Debit Order', 'Other'].map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Payment reference</Label>
                <Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="rounded-none mt-1" data-testid="payment-reference-input" />
              </div>
              {(payOpen?.payments || []).length > 0 && (
                <div className="border border-gray-200 divide-y divide-gray-100" data-testid="payment-history">
                  {payOpen.payments.map((p) => (
                    <div key={p.payment_id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-gray-600">{p.paid_on} · {p.method || '—'}{p.reference ? ` · ${p.reference}` : ''}</span>
                      <span className="font-medium">{money(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setPayOpen(null)} className="rounded-none">Cancel</Button>
                <Button onClick={recordPayment} disabled={busy || !payForm.amount} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="submit-payment-btn">
                  Record Payment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Capture / correct the amount on an uploaded invoice */}
        <Dialog open={!!amountOpen} onOpenChange={(open) => !open && setAmountOpen(null)}>
          <DialogContent className="sm:max-w-[420px]">
            <DialogHeader>
              <DialogTitle>Invoice Amount — {amountOpen?.invoice_number || amountOpen?.original_filename}</DialogTitle>
              <DialogDescription>
                Uploaded invoices need their value captured so they show correctly on statements and in totals.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-sm font-semibold">Amount (R, incl. VAT) *</Label>
                <Input
                  type="number" min="0" step="any" value={amountValue}
                  onChange={(e) => setAmountValue(e.target.value)}
                  className="rounded-none mt-1" data-testid="capture-amount-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setAmountOpen(null)} className="rounded-none">Cancel</Button>
                <Button
                  onClick={saveAmount} disabled={busy || !(Number(amountValue) > 0)}
                  className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none"
                  data-testid="save-amount-btn"
                >
                  Save Amount
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        <ConvertToClientDialog
          open={!!convertDoc}
          onOpenChange={(open) => !open && setConvertDoc(null)}
          doc={convertDoc}
          onConverted={fetchData}
        />

        <PdfPreviewDialog
          open={!!preview}
          onOpenChange={(open) => !open && setPreview(null)}
          url={preview?.url}
          title={preview?.title}
          filename={preview?.filename}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminInvoices;
